import { Trans, useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Anchor, Icon } from '@anytag/shared-atoms';
import { Maybe, TiktokAdAccountInfo, TiktokBusinessAccountInfo } from '@anytag/graphql';
import { TIKTOK_HELP_LINK } from '@anytag/shared-constants';
import { cn } from '@anytag/shared-utils';
import { styles, TikTokSectionTitle } from './styles';
import { CreateAdAccount } from './CreateAdAccountModal';
import { DisconnectAdAccountModal } from './DisconnectAdAccountModal';

interface TikTokAdAccountProps {
  tiktokBusinessAdAccounts?: Maybe<ReadonlyArray<TiktokAdAccountInfo>>;
  tiktokBusinessAccountInfo?: Maybe<TiktokBusinessAccountInfo>;
  hasAccountsLimitWarning?: boolean;
  isOnboardingFlow?: boolean;
}
export const TikTokAdAccount = ({
  isOnboardingFlow,
  tiktokBusinessAdAccounts,
  tiktokBusinessAccountInfo,
  hasAccountsLimitWarning
}: TikTokAdAccountProps) => {
  const { t } = useTranslation();
  const [disconnectAdAccountId, setDisconnectAdAccountId] = useState('');

  return (
    <div className={styles.section}>
      <TikTokSectionTitle
        index={3}
        isActive={!!tiktokBusinessAccountInfo?.bcList?.length}
        isConnected={!!tiktokBusinessAdAccounts?.length}
        title={t('TikTok Ad Account')}
      />
      {tiktokBusinessAccountInfo?.bcList?.length ? (
        <>
          <div>
            <Trans
              i18nKey="Create / connect your TikTok Ad Account"
              components={{
                a: <Anchor variant="blue" href={TIKTOK_HELP_LINK.needHelp} target="_blank" className="inline" />
              }}
            />
          </div>
          <CreateAdAccount
            bcList={tiktokBusinessAccountInfo?.bcList ?? []}
            hasAccountsLimitWarning={hasAccountsLimitWarning}
            className="mt-6"
          />
          {hasAccountsLimitWarning ? (
            <div className={styles.warning}>
              <Icon icon="warning" size={16} variant="yellow" />
              <span className="mx-2 my-0 font-semibold">{t('Attention')}</span>
              <Trans
                i18nKey="AdAccountsLimitWarning"
                components={{
                  a: (
                    <Anchor
                      variant="blue"
                      to={isOnboardingFlow ? '/tiktok-onboarding/select-plan' : '/tiktok/budget-allocation'}
                      className="ml-1"
                    />
                  )
                }}
              />
            </div>
          ) : null}
          <div
            className={cn('flex flex-col gap-4', {
              'mt-6': !hasAccountsLimitWarning
            })}
          >
            {tiktokBusinessAdAccounts?.map((adAccount) => {
              const isDisconnected = adAccount.status === 'DISCONNECT';

              return (
                <div key={adAccount.id} className={cn(styles.bcList, 'flex flex-row items-center gap-4 pr-3')}>
                  <div className={styles.adAccountChain(isDisconnected)}>
                    {isDisconnected ? (
                      <Icon icon="link-broken" size="14px" variant="gray" />
                    ) : (
                      <Icon icon="link-chain" size="14px" variant="green" />
                    )}
                  </div>
                  <div className={cn('flex flex-col gap-1', { 'opacity-30': isDisconnected })}>
                    <span className="text-base">{adAccount.name}</span>
                    <span className="text-sm text-tertiary">
                      {t('ID')}: {adAccount.id}
                    </span>
                    <span className="text-sm text-tertiary">
                      {t('Business Center')}: {adAccount.bcName}
                    </span>
                  </div>
                  <DisconnectAdAccountModal
                    hasAccountsLimitWarning={hasAccountsLimitWarning}
                    isOpen={disconnectAdAccountId === adAccount.id}
                    onClose={() => setDisconnectAdAccountId('')}
                    adAccount={adAccount}
                    onDisconnect={() => setDisconnectAdAccountId(adAccount.id)}
                    isDisconnected={isDisconnected}
                  />
                </div>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
};
